import { TFunction } from "i18next";
import { SideResponse } from "../../codegen-api";
import { COLORS } from "../../constants/design/colors";
import { formatCurrencyWithSubscript } from "../../utils/format";
import { PnL } from "../FundingHistory/style";
import { Direction } from "../shared/Table/style";

// ── Cell renderers (also exported for cases where a table needs to embed
//    a styled value inside a custom column) ───────────────────────────────

export const renderSignedAmount = (value?: string | number) => {
  if (value === undefined || value === null || value === "") return "-";
  const n = typeof value === "number" ? value : Number(value);
  if (!Number.isFinite(n) || n === 0) return "-";
  return (
    <PnL $color={n >= 0 ? COLORS.up.one : COLORS.down.one}>
      {formatCurrencyWithSubscript(value, { showPositiveSign: true })}
    </PnL>
  );
};

export const renderFeeAmount = (value?: string | number) => {
  if (value === undefined || value === null || value === "") return "-";
  const n = typeof value === "number" ? value : Number(value);
  if (!Number.isFinite(n) || n === 0) return "-";
  return formatCurrencyWithSubscript(-n, { showPositiveSign: true });
};

export const renderBuySell = (
  side: SideResponse | string,
  t: TFunction
) => (
  <Direction side={side === SideResponse.Buy ? SideResponse.Buy : SideResponse.Sell}>
    {side === SideResponse.Buy
      ? t("buy", { defaultValue: "Buy" })
      : t("sell", { defaultValue: "Sell" })}
  </Direction>
);

// ── Column factories ─────────────────────────────────────────────────────

/**
 * Known column-key → English label map. Avoids translation keys leaking
 * into the UI when an entry hasn't been added to the i18n bundle yet.
 */
const DEFAULT_LABELS: Record<string, string> = {
  market: "Market",
  side: "Side",
  type: "Type",
  size: "Size",
  size_filled: "Size / Filled",
  price: "Price",
  avg_price: "Avg Price",
  entry_price: "Entry Price",
  exit_price: "Exit Price",
  order_type: "Order Type",
  status: "Status",
  flags: "Flags",
  fills: "Fills",
  margin_type: "Margin",
  fees: "Fees",
  gross_pnl: "Gross P&L",
  net_pnl: "Net P&L",
  pnl: "P&L",
  time: "Time",
  closed: "Closed",
  opened: "Opened",
};

const humanize = (key: string) =>
  key
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());

const defaultLabelFor = (key: string, override?: string) =>
  override ?? DEFAULT_LABELS[key] ?? humanize(key);

export const titleFor = (t: TFunction, key: string, override?: string) =>
  t(key, { defaultValue: defaultLabelFor(key, override) });
